import { Drawer } from 'antd'
import type { DrawerProps } from 'antd'
import { useCallback, useState } from 'react'

export type ResizableDrawerProps = Omit<DrawerProps, 'width' | 'size'> & {
  /** Starting width when there's nothing in localStorage. Default 560. */
  defaultWidth?: number
  /** Disable the drag-to-resize handle. Defaults to `true`. */
  resizable?: boolean
  /** Persist user-resized width under this localStorage key. */
  storageKey?: string
  /** Hard caps. Defaults: min 320, no max. */
  minWidth?: number
  maxWidth?: number
}

const readStored = (key: string | undefined): number | undefined => {
  if (!key || typeof window === 'undefined') return undefined
  const raw = window.localStorage.getItem(key)
  if (!raw) return undefined
  const n = Number(raw)

  return Number.isFinite(n) && n > 0 ? n : undefined
}

/**
 * antd v6 Drawer wrapper with a working drag-to-resize handle.
 *
 * Why this exists: passing `width` to antd's <Drawer> resolves to `size`, which
 * is controlled and overrides the internal `currentSize` that the drag handle
 * updates — so dragging would do nothing. We pass `defaultSize` instead (used
 * only as the initial value) and observe drag via `onResize` for persistence.
 */
export const ResizableDrawer = ({
  defaultWidth = 560,
  resizable = true,
  storageKey,
  minWidth,
  maxWidth,
  ...drawerProps
}: ResizableDrawerProps) => {
  const [initialSize] = useState<number>(
    () => readStored(storageKey) ?? defaultWidth
  )

  const handleResize = useCallback(
    (next: number) => {
      if (!storageKey || typeof window === 'undefined') return
      const clamped = Math.max(
        minWidth ?? 0,
        maxWidth != null ? Math.min(next, maxWidth) : next
      )
      window.localStorage.setItem(storageKey, String(clamped))
    },
    [storageKey, minWidth, maxWidth]
  )

  return (
    <Drawer
      {...drawerProps}
      defaultSize={initialSize}
      maxSize={maxWidth}
      resizable={resizable ? { onResize: handleResize } : false}
    />
  )
}
